/**
 * Converts various file-sharing URLs into direct image URLs
 * that can be used in <img> tags.
 *
 * Supported services:
 * - Google Drive (drive.google.com)
 * - Dropbox (dropbox.com)
 * - OneDrive (onedrive.live.com, 1drv.ms)
 * - iCloud (icloud.com)
 * - Google Photos (photos.google.com, lh3.googleusercontent.com)
 * - Imgur (imgur.com)
 * - Direct image URLs (jpg, jpeg, png, gif, webp, svg, bmp, ico) — passed through
 *
 * @param {string} url - The original URL
 * @returns {string} - A direct image URL, or the original if unrecognized
 */
export function convertImageUrl(url) {
  if (!url || typeof url !== 'string') return url;

  const trimmed = url.trim();

  // Google Drive
  // Formats:
  //   https://drive.google.com/file/d/FILE_ID/view?usp=sharing
  //   https://drive.google.com/open?id=FILE_ID
  //   https://drive.google.com/uc?id=FILE_ID&export=download
  const driveFileMatch = trimmed.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (driveFileMatch) {
    return `https://drive.google.com/uc?export=view&id=${driveFileMatch[1]}`;
  }
  const driveOpenMatch = trimmed.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (driveOpenMatch) {
    return `https://drive.google.com/uc?export=view&id=${driveOpenMatch[1]}`;
  }
  const driveUcMatch = trimmed.match(/drive\.google\.com\/uc\?.*id=([a-zA-Z0-9_-]+)/);
  if (driveUcMatch && !trimmed.includes('export=view')) {
    return `https://drive.google.com/uc?export=view&id=${driveUcMatch[1]}`;
  }

  // Dropbox
  // https://www.dropbox.com/s/XXXXX/file.jpg?dl=0 → ...?raw=1
  if (trimmed.includes('dropbox.com')) {
    return trimmed.replace(/[?&]dl=0/, '?raw=1');
  }

  // OneDrive
  // https://onedrive.live.com/embed?... → pass through (embeds work)
  // https://1drv.ms/... → convert to embed
  if (trimmed.includes('1drv.ms')) {
    // 1drv.ms short links need the embed format
    const encoded = btoa(trimmed).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return `https://api.onedrive.com/v1.0/shares/u!${encoded}/root/content`;
  }

  // Google Photos / googleusercontent
  // lh3.googleusercontent.com links are already direct — just make sure size param is set
  if (trimmed.includes('lh3.googleusercontent.com')) {
    // Append =w1200 if no size param
    if (!trimmed.includes('=w') && !trimmed.includes('=s')) {
      return `${trimmed}=w1200`;
    }
    return trimmed;
  }

  // Imgur
  // https://imgur.com/XXXXX → https://i.imgur.com/XXXXX.jpg
  const imgurMatch = trimmed.match(/imgur\.com\/(?:a\/)?([a-zA-Z0-9]+)$/);
  if (imgurMatch && !trimmed.includes('i.imgur.com')) {
    return `https://i.imgur.com/${imgurMatch[1]}.jpg`;
  }

  // Already a direct image URL — pass through
  if (/\.(jpe?g|png|gif|webp|svg|bmp|ico)(\?.*)?$/i.test(trimmed)) {
    return trimmed;
  }

  // Unrecognized — return as-is
  return trimmed;
}
